import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import styles from './styles.module.css';
import QRContent from './QRContent';
import BluetoothContent from './BluetoothContent';
import { secondaryModalState } from '../../../state-observables/modal/ModalState';
import { FaSpinner, FaTrashAlt } from 'react-icons/fa';
import { CustomBluetoothItem } from '../../../types/userDeviceInfo';
import Logger from '../../../services/logger/Logger';

const SHOW_LOGS = true;
const TAG = "ServiceProgramAddMoreRobots";

function ServiceProgramAddMoreRobots() {
  const { t } = useTranslation();
  const [content, setContent] = useState<"bluetooth" | "qr">("qr");
  const [items, setItems] = useState<(CustomBluetoothItem)[]>([]);
  const [isLoadingMessage, setIsLoadingMessage] = useState("");

  // const { connectedRafts } = useConnectedRafts();
  // const selectedRaft = connectedRafts.find(connectedRaft => connectedRaft.isSelected && connectedRaft.id !== NewRobotIdE.NEW);

  const scanForNewDevice = async () => {
    const connectedRafts = window.applicationManager.connectedRafts;
    const existingDevices: CustomBluetoothItem[] = [];
    for (const raftKey in connectedRafts) {
      const raft = connectedRafts[raftKey];
      const customDevice: CustomBluetoothItem = {
        id: raft.id,
        name: raft.getFriendlyName() || "",
        serialNumber: raft.getSerialNumber() || "",
        type: raft.type,
      };
      existingDevices.push(customDevice);
    }
    existingDevices.push(...items);

    try {
      setIsLoadingMessage("Scanning for new devices...");
      const customDevices = await window.applicationManager.scanForPotentialWarrantyDevice(existingDevices)
      // find the difference between the devices that are already connected and the new devices
      const existingIds = new Set(items.map(item => item.id));
      const newDevices = customDevices.filter(device => !existingIds.has(device.id));
      if (newDevices.length < 1) {
        window.applicationManager.toaster.error("No new devices found.");
        return;
      }
      const newDeviceNames = newDevices.map(device => device.name).join(", ");
      window.applicationManager.toaster.success(`${newDeviceNames} added to the list of robots.`);
      setItems((prevItems) => [...prevItems, ...newDevices]);

      Logger.info(SHOW_LOGS, TAG, "New devices added: ", newDeviceNames);

    } catch (error) {
      Logger.error(SHOW_LOGS, TAG, "Error scanning for new devices: ", error);
      window.applicationManager.toaster.error("Something went wrong. Please try again.");
    } finally {
      setIsLoadingMessage("");
    }
    return;
  }


  const removeItem = (itemToBeRemoved: CustomBluetoothItem) => {
    setItems((oldItems) => {
      return oldItems.filter((device) => device.serialNumber !== itemToBeRemoved.serialNumber);
    });
    const nameOrSerial = typeof itemToBeRemoved === "string" ? "Serial Number" : "Robot";
    window.applicationManager.toaster.success(`${nameOrSerial} removed from the list.`, {
      autoClose: 1000,
    });
  };

  const onCancel = () => {
    secondaryModalState.closeModal([]);
  };

  const onSubmit = () => {
    secondaryModalState.closeModal(items);
  };

  const onBluetoothContent = () => {
    setContent("bluetooth");
  }

  const onQRContent = () => {
    setContent("qr");
  }

  return (
    <div className={styles.container}>
      {content === "qr" ?
        <QRContent setItems={setItems} />
        :
        <BluetoothContent />
      }
      <ul className={styles.deviceList}>
        <h3>{t('service_program.serial_names_heading')}</h3>
        {isLoadingMessage && (
          <div className={styles.scanningForMoreRobots}>
            <h5>
              {isLoadingMessage} <FaSpinner />
            </h5>
          </div>
        )}
        {items.map((item) => {
          const key = item.serialNumber;
          return (
            <li key={key} className={styles.deviceItem}>
              {item.name || item.serialNumber}
              <span className={styles.removeButton} onClick={() => removeItem(item)}>
                <FaTrashAlt />
              </span>
            </li>
          )
        })}
      </ul>
      <div className={styles.buttonsContainer}>
        <button type="button" className={styles.submitButton} onClick={onSubmit}>{t('service_program.submit')}</button>
        <button type="button" className={styles.cancelButton} onClick={onCancel}>{t('service_program.cancel')}</button>
        {content === "bluetooth" && <button className={styles.addAnotherRobotButton} onClick={scanForNewDevice}>{t('service_program.add_another_robot_bt')}</button>}
        {content === "qr" && <button className={styles.addRobotsBtButton} onClick={onBluetoothContent}>{t('service_program.add_robots_bt')}</button>}
        {content === "bluetooth" && <button type="button" className={styles.addRobotsQRButton} onClick={onQRContent}>{t('service_program.add_robots_qr')}</button>}
      </div>
    </div>
  );
};

export default ServiceProgramAddMoreRobots;
